import * as React from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';

import { CommonDirection } from 'shared/entities/common/client';
import { isHorizontalFadeHidden } from 'shared/entities/common/utils/horizontalScrollEdge';

import './FadeScroll.modules.scss';

type Props = React.PropsWithChildren<{
  withoutFade?: boolean;
  id?: string;
  shadowPosition?: CommonDirection.bottom | CommonDirection.right;
  startShadowPosition?: CommonDirection.top | CommonDirection.left;
  boxShadowCompensation?: CommonDirection;
  contentClassname?: string;
  className?: string;
}>;

export const BoxShadowCompensation: Record<CommonDirection, string> = {
  top: 'container__content_box-shadow-correct-top',
  bottom: 'container__content_box-shadow-correct-bottom',
  left: 'container__content_box-shadow-correct-left',
  right: 'container__content_box-shadow-correct-right'
};

const FadeScroll = React.forwardRef(
  (
    {
      children,
      id,
      withoutFade = false,
      shadowPosition = CommonDirection.bottom,
      startShadowPosition,
      boxShadowCompensation,
      contentClassname,
      className
    }: Props,
    ref: React.MutableRefObject<HTMLDivElement>
  ) => {
    const [showFade, setShowFade] = React.useState<boolean>(false);
    const [showStartFade, setShowStartFade] = React.useState<boolean>(false); // тень в начале списка
    const wrapperRef = ref ?? React.useRef<HTMLDivElement | null>(null);

    const handleScroll = React.useCallback(
      throttle(() => {
        if (!wrapperRef.current) {
          return;
        }
        const element = wrapperRef.current;

        let hasScroll = false;
        let scrolledToEnd = false;
        const tolerance = 1; // Погрешность для округления пикселей

        // показываем fade только, если есть скролл и не дошли до конца при скролле
        switch (shadowPosition) {
          case CommonDirection.bottom:
            hasScroll = element.scrollHeight > element.clientHeight;
            scrolledToEnd =
              element.scrollTop + element.offsetHeight >=
              element.scrollHeight - tolerance;
            break;
          case CommonDirection.right:
            hasScroll = element.scrollWidth > element.clientWidth;
            scrolledToEnd = isHorizontalFadeHidden(
              element.scrollLeft,
              element.scrollWidth,
              element.offsetWidth,
              element.clientWidth
            );
            break;
        }

        setShowFade(hasScroll && !scrolledToEnd);

        if (startShadowPosition) {
          let scrolledToStart = false;

          switch (startShadowPosition) {
            case CommonDirection.top:
              hasScroll = element.scrollHeight > element.clientHeight;
              scrolledToStart = element.scrollTop <= tolerance;
              break;
            case CommonDirection.left:
              hasScroll = element.scrollWidth > element.clientWidth;
              scrolledToStart = element.scrollLeft <= tolerance;
              break;
          }

          setShowStartFade(hasScroll && !scrolledToStart);
        }
      }, 100),
      [shadowPosition, startShadowPosition]
    );

    React.useEffect(() => {
      let observer: ResizeObserver | null = null;

      if (wrapperRef.current) {
        observer = new ResizeObserver(handleScroll);

        observer.observe(wrapperRef.current);
        handleScroll();
      }

      return () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }, []);

    return (
      <div
        styleName={cn(
          'container',
          showFade && !withoutFade && `container_fade_${shadowPosition}`,
          showStartFade && `container_fade_${startShadowPosition}`,
          boxShadowCompensation && BoxShadowCompensation[boxShadowCompensation]
        )}
        className={className}
      >
        <div
          ref={wrapperRef}
          styleName={cn(
            `container__content_${
              shadowPosition === CommonDirection.right ||
              startShadowPosition === CommonDirection.left
                ? 'horizontal'
                : 'vertical'
            }`,
            boxShadowCompensation &&
              BoxShadowCompensation[boxShadowCompensation]
          )}
          className={contentClassname}
          onScroll={handleScroll}
          id={id}
        >
          {children}
        </div>
      </div>
    );
  }
);

export default FadeScroll;
